/**
 * Générateur, Attendu 180, « Il utilise un rapport d'agrandissement ou de
 * réduction pour calculer des longueurs, des aires, des volumes. »
 * Niveau : 4ème | Thème #9 Transformations | Grandeurs et mesures.
 *
 * PLAFOND (attendus de fin de 4e, Eduscol, `.claude/reference/eduscol-attendus-fin-4e.md`
 * l. 334-350, rubrique « Comprendre l'effet de quelques transformations sur les
 * figures géométriques ») :
 *   l. 334-335 « Il utilise un rapport d'agrandissement ou de réduction pour
 *   calculer, des longueurs, des aires, des volumes. »
 *   l. 344-345 (exemple de réussite) « Il calcule la longueur d'une arête,
 *   l'aire d'une face et le volume de l'agrandissement ou de la réduction d'un
 *   solide du programme avec une échelle donnée. »
 *   l. 347-350 (exemple d'énoncé) « Un pavé droit a les dimensions suivantes :
 *   L = 12 cm, l = 6 cm, h = 4 cm. Donne les aires de chacune de ses faces,
 *   puis le volume du solide considéré. On décide de réduire au tiers toutes
 *   les dimensions du pavé droit. Calcule alors les aires de chacun des
 *   surfaces, puis le volume du nouveau pavé droit. »
 * Cours du professeur (19Espace2 p. 3-4) : « Par un agrandissement ou une
 * réduction de rapport k, les longueurs sont multipliées par k, les aires par
 * k², les volumes par k³. » Le mot est « rapport » (jamais « coefficient »,
 * « facteur », « homothétie », « centre O »).
 *
 * Historique : sept cartes de juillet 2026 (longueur × k, aire × k², volume
 * × k³ direct ou inverse, retrouver k, « De quel facteur ? », le pavé, l'échelle
 * d'une maquette), jamais revues, fermées côté prof.
 *
 * ── Revue 2026-09-25 (vague 7 « sept objectifs 4e fermés », décisions du
 * coordinateur, `scratchpad/vague7/DECISIONS_180-181.md`, à partir du cadrage
 * `.claude/agent-memory/programme-maths/project_cadrage_180-181.md` et de
 * l'audit `audit_180-181.md`) ─────────────────────────────────────────────
 * SORTIES (et leurs raisons) :
 *   - « Agrandir ou réduire une longueur » (longueur_saisie) : la longueur
 *     seule est la ligne de 174 (l. 277-278, longueur manquante dans un
 *     agrandissement) ; en 180, la longueur n'existe que comme ARÊTE du solide
 *     (l. 344), servie par la carte du pavé.
 *   - « Retrouver le rapport » (retrouver_k_qcm) : hors ligne (l. 334 DONNE le
 *     rapport) ; l'aire → k est 151 (racine carrée), le volume → k demande une
 *     racine cubique absente du cycle 4 ; clé toujours 2e plus petite (100 %).
 *   - « Échelle d'une maquette » (echelle_texte_trou) : proportionnalité et
 *     conversion cm → m (172, 1247), méthode soufflée dans la consigne (100 %).
 *   - Le sens INVERSE du volume (49,3 % des tirages : l'image donnée, le départ
 *     cherché) : l'exemple de réussite est direct ; sens direct seul.
 * RAPPORTS ADMIS : k ∈ {2, 3, 4, 5} et {1/2, 1/3, 1/4, 1/5}, écrits en KaTeX
 * (tex / fracTex) ou en mots une fois sur deux quand le mot existe (« On double
 * toutes ses dimensions. », « On triple… », « On réduit de moitié / au tiers /
 * au quart… ») ; jamais 2/3, 3/2, décimal, négatif.
 * QUATRE CARTES, un format chacune (aucun onglet affiché) ; rappel brouillon
 * ET calculatrice (contenu.calculatrice = true) sur les quatre : un élève de 4e
 * ne fait pas 36 × 25, 12 × 125 ni 24 × 15 × 9 ÷ 27 à la main, l'attendu teste
 * k, k², k³, pas la multiplication. Réponses entières par construction.
 *   1. « Calculer l'aire d'une figure agrandie ou réduite » (aire_texte,
 *      Saisie) : une figure plane ou une face nommée, aire A ≤ 100 donnée,
 *      rapport donné, réponse A × k² entière (A multiple de d²) et ≤ 500.
 *   2. « Calculer le volume d'un solide agrandi ou réduit » (volume_texte,
 *      Saisie) : un solide du programme nommé (cube, pavé droit, prisme,
 *      cylindre, pyramide, cône ; « une pyramide », « un cône » : accord), volume
 *      V donné en nombre (jamais π à taper) ; agrandissement : V ≤ 200 et
 *      réponse ≤ 2 000 ; réduction : V = m × d³ ≤ 2 000 avec une réponse m de 4
 *      à 40 cm³ (coordinateur 25/09 : pas de solide réduit à 1 cm³).
 *   3. « Longueur, aire ou volume : par combien ? » (facteur_qcm_unique, QCM,
 *      LE QCM de l'attendu) : QCM CHIFFRÉ, la question tirée sur l'une des
 *      trois grandeurs (un tiers chacune) pour que le rang de la clé dépende
 *      de la question ; options = clé + trois erreurs nommées (contrat
 *      REGLES_DISTRACTEURS_QCM ci-dessous, codes dans _meta.params) ; quatre
 *      valeurs deux à deux distinctes par construction (throw sinon).
 *      POSITION DE LA CLÉ, mesurée sur 3 000 tirages (gen180.self.test.js) :
 *      longueur → la plus petite des quatre (100 %), aire → la troisième
 *      (100 %), volume → la plus grande (100 %) ; résidu assumé par la
 *      décision : un élève qui coche toujours la plus grande réussit un tiers.
 *      Écart à la décision, assumé et signalé : k = 2 n'est pas tiré pour la
 *      longueur non plus (L × 2k = L × k² quand k = 2 : deux options coïncident,
 *      et la décision demande de retirer le tirage entier, jamais de boucher) ;
 *      donc k ∈ {3, 4, 5} pour la longueur et l'aire, k ∈ {4, 5} pour le volume
 *      (k = 3 : V × 3k = V × k²).
 *   4. « Le pavé réduit ou agrandi » (pave_texte_trou_saisie, Saisie trous,
 *      l'exemple d'énoncé l. 347-350) : « Un pavé droit a pour dimensions L, l,
 *      h. On réduit au tiers (de moitié, au quart) toutes ses dimensions / On
 *      double, on triple… » puis, selon contenu.sousType ∈ {arete, face,
 *      volume}, DEUX trous nommés par leur phrase (la grandeur de départ, puis
 *      la même grandeur du nouveau pavé), jamais une liste de trous
 *      interchangeables, jamais un trou « divisée par 9 » (pur rappel). Les
 *      phrases à trous n'ont pas de `$` : les unités et les lettres L, l, h y
 *      sont en clair. Dimensions L > l > h ≤ 12, multiples du diviseur, toutes
 *      différentes ; l'arête, la face (trois faces possibles) sont tirées, le
 *      volume image est plafonné à quatre chiffres (≤ 9 999 ; borne ajoutée,
 *      signalée : sans elle « on triple » un pavé 12 × 11 × 10 donnait 35 640).
 * SACRIFIÉS (exemples réservés au cours et à la fiche, jamais tirés) : le pavé
 * (12 ; 6 ; 4) du plafond dans tous les ordres ; (A = 12 cm² ; k = 2) sur la
 * carte aire et le QCM ; (A = 72 cm² ; k = 1/3), l'aire de la grande face du
 * pavé du plafond réduite au tiers, sur la carte aire ; le cône du cours
 * (144π, k = 0,375) et le volume 288 sont impossibles par construction (pas de
 * π, pas de décimal, V ≤ 200 en agrandissement, réponse ≥ 4 cm³ en réduction).
 * Solveurs test-side : tests/solvers/transformations-4e.js (les quatre cartes,
 * recalcul depuis le texte rendu, le rapport lu en KaTeX ou en mots).
 */

import {
    randInt, randomChoice, shuffle, tex, fracTex, makeSaisie, makeQcm, makeTexteTrou,
} from '../../../utils';

const ATTENDU_ID = 180;
const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_AIRE = 'Calculer l\'aire d\'une figure agrandie ou réduite';
const L_VOL = 'Calculer le volume d\'un solide agrandi ou réduit';
const L_FACT = 'Longueur, aire ou volume : par combien ?';
const L_PAVE = 'Le pavé réduit ou agrandi';

const RAPPEL_BROUILLON = '<p class="italic text-slate-400">✏️ Pose tes calculs au brouillon.</p>';
/** La calculatrice de l'écran (décision 2026-09-25 : sur les quatre cartes) ; lue par calculatriceAutorisee. */
const calculatrice = (payload) => ({ ...payload, contenu: { ...payload.contenu, calculatrice: true } });

// ═════════ Les rapports admis et leurs écritures ═════════

/** Les huit rapports admis (décision 2026-09-25) : jamais 2/3, 3/2, décimal, négatif. */
export const RAPPORTS = [
    { n: 2, d: 1 }, { n: 3, d: 1 }, { n: 4, d: 1 }, { n: 5, d: 1 },
    { n: 1, d: 2 }, { n: 1, d: 3 }, { n: 1, d: 4 }, { n: 1, d: 5 },
];
const kStr = ({ n, d }) => (d === 1 ? String(n) : `${n}/${d}`);
const agrandit = (k) => k.d === 1;
const memeK = (k, s) => kStr(k) === s;

/** Le verbe de l'écriture en mots, quand elle existe (4, 5 et 1/5 n'en ont pas). */
const MOTS = { 2: 'double', 3: 'triple', '1/2': 'réduit de moitié', '1/3': 'réduit au tiers', '1/4': 'réduit au quart' };

/** KaTeX ou mots une fois sur deux quand le mot existe, KaTeX sinon. */
const tirerEcriture = (k) => (MOTS[kStr(k)] && randInt(0, 1) === 0 ? 'mots' : 'katex');

/**
 * La phrase du rapport. cible = { pronom: true } : « On lui applique un
 * agrandissement de rapport 3. » / « On triple toutes ses dimensions. » ;
 * cible = { au: 'au cube', du: 'du cube' } : « On applique au cube … » /
 * « On triple toutes les dimensions du cube. »
 */
const phraseRapport = (k, ecriture, cible) => {
    if (ecriture === 'mots') {
        const verbe = MOTS[kStr(k)];
        if (!verbe) throw new Error(`gen_180 : pas de mot pour le rapport ${kStr(k)}`);
        return cible.pronom ? `On ${verbe} toutes ses dimensions.` : `On ${verbe} toutes les dimensions ${cible.du}.`;
    }
    const transfo = agrandit(k)
        ? `un agrandissement de rapport ${tex(String(k.n))}`
        : `une réduction de rapport ${fracTex(k.n, k.d)}`;
    return cible.pronom ? `On lui applique ${transfo}.` : `On applique ${cible.au} ${transfo}.`;
};

const adjectif = (k, feminin) => (agrandit(k) ? 'agrandi' : 'réduit') + (feminin ? 'e' : '');
const apres = (k) => (agrandit(k) ? 'après l\'agrandissement' : 'après la réduction');

// ═════════ Les supports ═════════

/** Figures planes : { nom, f } (f : féminin). */
const FIGURES = [
    { nom: 'triangle', f: false }, { nom: 'rectangle', f: false }, { nom: 'carré', f: false }, { nom: 'figure', f: true },
];
/** Faces nommées d'un solide, pour la carte aire. */
const FACES = [
    { solide: 'cube', au: 'au cube', du: 'du cube' },
    { solide: 'pavé droit', au: 'au pavé droit', du: 'du pavé droit' },
];
/** Les solides du programme (l. 344-345 : « un solide du programme »), avec l'accord. */
const SOLIDES = [
    { nom: 'cube', f: false }, { nom: 'pavé droit', f: false }, { nom: 'prisme', f: false },
    { nom: 'cylindre', f: false }, { nom: 'pyramide', f: true }, { nom: 'cône', f: false },
];
const art = (o) => (o.f ? 'Une' : 'Un');
const du = (o) => (o.f ? 'de la' : 'du');
const le = (o) => (o.f ? 'la' : 'le');

// ═════════ Carte 1 : « Calculer l'aire d'une figure agrandie ou réduite » (Saisie) ═════════

const A_MAX = 100;
const AIRE_IMAGE_MAX = 500;
/** (A ; k) sacrifiés : (12 ; 2) de la fiche, (72 ; 1/3) la grande face du pavé du plafond réduite au tiers. */
const SACRIFIES_AIRE = [{ A: 12, k: '2' }, { A: 72, k: '1/3' }];
const aireSacrifiee = (A, k) => SACRIFIES_AIRE.some((s) => s.A === A && memeK(k, s.k));

/** A ≤ 100, entier ; multiple de d² (m ≥ 2) pour une réduction ; A × k² ≤ 500 pour un agrandissement. */
const tirerAire = (k) => {
    if (agrandit(k)) return randInt(2, Math.min(A_MAX, Math.floor(AIRE_IMAGE_MAX / (k.n * k.n))));
    const d2 = k.d * k.d;
    const mMax = Math.floor(A_MAX / d2);
    return randInt(Math.min(2, mMax), mMax) * d2;
};

const payloadAire = (k, A, support, ecriture) => {
    const d2 = k.d * k.d;
    if (A % d2 !== 0 || A > A_MAX || A < 1 || aireSacrifiee(A, k)) throw new Error(`gen_180 aire : tirage ${A} × ${kStr(k)}`);
    const rep = (A / d2) * k.n * k.n;
    // Second chemin : l'aire de l'image est A × k × k, en fraction, et elle est entière.
    if (rep * d2 !== A * k.n * k.n || !Number.isInteger(rep) || rep < 1 || rep > AIRE_IMAGE_MAX) throw new Error('gen_180 aire : réponse');
    let situation; let question;
    if (support.solide) {
        situation = `Une face d'un ${support.solide} a une aire de <b>${A} cm²</b>. ${phraseRapport(k, ecriture, support)}`;
        question = `Quelle est l'aire de cette face sur le ${support.solide} ${adjectif(k, false)} ?`;
    } else {
        situation = `${art(support)} ${support.nom} a une aire de <b>${A} cm²</b>. ${phraseRapport(k, ecriture, { pronom: true })}`;
        question = `Quelle est l'aire ${du(support)} ${support.nom} ${adjectif(k, support.f)} ?`;
    }
    const payload = makeSaisie(String(rep),
        `<p>${situation}</p>${RAPPEL_BROUILLON}<p><b>${question}</b></p>`,
        { ...opts(L_AIRE), suffixe: 'cm²' });
    payload._meta.params = { support: support.solide ? `face d'un ${support.solide}` : support.nom, A, k: kStr(k), ecriture, reponse: rep };
    return calculatrice(payload);
};

const genAire = () => {
    for (let essai = 0; essai < 100; essai++) {
        const k = randomChoice(RAPPORTS);
        const A = tirerAire(k);
        if (aireSacrifiee(A, k)) continue;
        const support = randInt(0, 2) === 0 ? randomChoice(FACES) : randomChoice(FIGURES);
        return payloadAire(k, A, support, tirerEcriture(k));
    }
    // Repli déterministe, vérifié par payloadAire : 25 cm² doublé, 100 cm².
    return payloadAire({ n: 2, d: 1 }, 25, FIGURES[0], 'katex');
};

// ═════════ Carte 2 : « Calculer le volume d'un solide agrandi ou réduit » (Saisie) ═════════

// Agrandissement : V ≤ 200 et V × k³ ≤ 2 000. Réduction : le solide réduit garde
// un volume lisible (au moins 4 cm³, coordinateur 25/09 : 125 → 1 cm³ faisait
// une réponse dérisoire), donc V = m × d³ avec m de 4 à 40, V ≤ 2 000.
const V_MAX = 200;
const VOLUME_IMAGE_MAX = 2000;
const V_MAX_REDUIT = 2000;
const REPONSE_REDUIT_MIN = 4;
const REPONSE_REDUIT_MAX = 40;

/** Entier ; agrandissement : 2 ≤ V ≤ 200 avec V × k³ ≤ 2 000 ; réduction : V = m × d³, 4 ≤ m ≤ 40, V ≤ 2 000. */
const tirerVolume = (k) => {
    if (agrandit(k)) return randInt(2, Math.min(V_MAX, Math.floor(VOLUME_IMAGE_MAX / (k.n ** 3))));
    const d3 = k.d ** 3;
    const mMax = Math.min(REPONSE_REDUIT_MAX, Math.floor(V_MAX_REDUIT / d3));
    return randInt(REPONSE_REDUIT_MIN, mMax) * d3;
};

const payloadVolume = (k, V, solide, ecriture) => {
    const d3 = k.d ** 3;
    if (V % d3 !== 0 || V > (agrandit(k) ? V_MAX : V_MAX_REDUIT) || V < 1) throw new Error(`gen_180 volume : tirage ${V} × ${kStr(k)}`);
    const rep = (V / d3) * k.n ** 3;
    // Second chemin : V × k × k × k en fraction, entier, borné.
    if (rep * d3 !== V * k.n * k.n * k.n || !Number.isInteger(rep) || rep < (agrandit(k) ? 1 : REPONSE_REDUIT_MIN) || rep > VOLUME_IMAGE_MAX) throw new Error('gen_180 volume : réponse');
    const payload = makeSaisie(String(rep),
        `<p>${art(solide)} ${solide.nom} a un volume de <b>${V} cm³</b>. ${phraseRapport(k, ecriture, { pronom: true })}</p>`
        + `${RAPPEL_BROUILLON}<p><b>Quel est le volume ${du(solide)} ${solide.nom} ${adjectif(k, solide.f)} ?</b></p>`,
        { ...opts(L_VOL), suffixe: 'cm³' });
    payload._meta.params = { solide: solide.nom, V, k: kStr(k), ecriture, reponse: rep };
    return calculatrice(payload);
};

const genVolume = () => {
    const k = randomChoice(RAPPORTS);
    return payloadVolume(k, tirerVolume(k), randomChoice(SOLIDES), tirerEcriture(k));
};

// ═════════ Carte 3 : « Longueur, aire ou volume : par combien ? » (QCM chiffré) ═════════

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'QCM CHIFFRÉ (décision 2026-09-25) : la grandeur de départ et le rapport sont donnés, l\'élève calcule la grandeur de l\'image ; « Par combien ? » sans nombre serait un pur rappel de la propriété',
        'la question est tirée sur l\'une des TROIS grandeurs, un tiers chacune, pour que le rang de la clé dépende de la question (longueur : la plus petite ; aire : la troisième ; volume : la plus grande, résidu assumé)',
        'agrandissements seulement : k ∈ {3, 4, 5} pour la longueur et l\'aire, k ∈ {4, 5} pour le volume, parce qu\'avec k = 2 (L × 2k = L × k²) et k = 3 en volume (V × 3k = V × k²) deux options coïncident et la décision retire le tirage entier, jamais ne bouche',
        'quatre valeurs deux à deux distinctes par construction (throw sinon) ; codes dans _meta.params.distracteurs ; le rapport en KaTeX ou en mots une fois sur deux (k = 3 : « On triple toutes ses dimensions. »)',
        'sacrifié : (aire 12 cm² ; rapport 2), l\'exemple réservé à la fiche, jamais tiré (k = 2 absent)',
    ],
    facteur_qcm_unique: {
        support: 'un côté d\'une figure plane (2 à 9 cm), l\'aire d\'une figure plane (2 à 16 cm²) ou le volume d\'un solide du programme (2 à 16 cm³), agrandi de rapport k',
        cle: 'longueur × k, aire × k², volume × k³',
        familles: {
            longueur: {
                exposant_en_trop: 'L × k² et L × k³ : l\'exposant des aires ou des volumes appliqué à une longueur (deux options, même famille)',
                base_fois_exposant: 'L × 2k : k² lu 2k (nom de 1103)',
            },
            aire: {
                aire_fois_k: 'A × k : l\'aire multipliée par k au lieu de k², la conception linéaire (PRINCIPALE, cadrage)',
                exposant_echange: 'A × k³ : les deux exposants connus mais permutés',
                base_fois_exposant: 'A × 2k : k² lu 2k',
            },
            volume: {
                volume_fois_k: 'V × k : le volume multiplié par k au lieu de k³',
                exposant_echange: 'V × k² : les exposants permutés',
                base_fois_exposant: 'V × 3k : k³ lu 3k',
            },
        },
        supprimees: {
            rapport_deux: 'k = 2 : 2k = k², deux options identiques (tirage retiré, jamais bouché)',
            rapport_trois_volume: 'k = 3 en volume : 3k = k², idem',
            reductions: 'k = 1/d : A ÷ 6 ou V ÷ 9 ne sont pas entiers sans contraindre A à un multiple de 54 ; le QCM reste sur les agrandissements (décision)',
        },
        residu: 'mesuré sur 3 000 tirages (gen180.self.test.js) : longueur → clé la plus petite (100 %), aire → troisième (100 %), volume → la plus grande (100 %) ; la grandeur étant tirée au tiers, cocher toujours la plus grande réussit un tiers',
    },
};

const GRANDEURS = ['longueur', 'aire', 'volume'];
const K_QCM = { longueur: [3, 4, 5], aire: [3, 4, 5], volume: [4, 5] };
const X_MAX = { longueur: 9, aire: 16, volume: 16 };

/** La clé et les trois erreurs nommées, pour une grandeur x et un rapport entier k. */
const optionsFacteur = (grandeur, x, k) => {
    const k2 = k * k; const k3 = k2 * k;
    if (grandeur === 'longueur') {
        return { cle: x * k, distracteurs: [
            { code: 'exposant_en_trop', valeur: x * k2 }, { code: 'exposant_en_trop', valeur: x * k3 }, { code: 'base_fois_exposant', valeur: x * 2 * k },
        ] };
    }
    if (grandeur === 'aire') {
        return { cle: x * k2, distracteurs: [
            { code: 'aire_fois_k', valeur: x * k }, { code: 'exposant_echange', valeur: x * k3 }, { code: 'base_fois_exposant', valeur: x * 2 * k },
        ] };
    }
    return { cle: x * k3, distracteurs: [
        { code: 'volume_fois_k', valeur: x * k }, { code: 'exposant_echange', valeur: x * k2 }, { code: 'base_fois_exposant', valeur: x * 3 * k },
    ] };
};

const payloadFacteur = (grandeur, x, k, support, ecriture) => {
    if (!K_QCM[grandeur].includes(k) || x < 2 || x > X_MAX[grandeur]) throw new Error(`gen_180 facteur : tirage ${grandeur} ${x} ${k}`);
    if (grandeur === 'aire' && x === 12 && k === 2) throw new Error('gen_180 facteur : sacrifié');
    const { cle, distracteurs } = optionsFacteur(grandeur, x, k);
    // Second chemin : la clé par multiplications successives (une par exposant).
    let cle2 = x;
    for (let e = 0; e < { longueur: 1, aire: 2, volume: 3 }[grandeur]; e++) cle2 *= k;
    if (cle2 !== cle) throw new Error('gen_180 facteur : clé');
    const valeurs = [cle, ...distracteurs.map((d) => d.valeur)];
    if (new Set(valeurs).size !== 4) throw new Error(`gen_180 facteur : options non distinctes ${valeurs.join(' ')}`);
    const kk = { n: k, d: 1 };
    let situation; let question; let unite;
    if (grandeur === 'longueur') {
        situation = `${art(support)} ${support.nom} a un côté qui mesure <b>${x} cm</b>. ${phraseRapport(kk, ecriture, { pronom: true })}`;
        question = `Quelle est la longueur de ce côté sur ${le(support)} ${support.nom} ${adjectif(kk, support.f)} ?`;
        unite = 'cm';
    } else if (grandeur === 'aire') {
        situation = `${art(support)} ${support.nom} a une aire de <b>${x} cm²</b>. ${phraseRapport(kk, ecriture, { pronom: true })}`;
        question = `Quelle est l'aire ${du(support)} ${support.nom} ${adjectif(kk, support.f)} ?`;
        unite = 'cm²';
    } else {
        situation = `${art(support)} ${support.nom} a un volume de <b>${x} cm³</b>. ${phraseRapport(kk, ecriture, { pronom: true })}`;
        question = `Quel est le volume ${du(support)} ${support.nom} ${adjectif(kk, support.f)} ?`;
        unite = 'cm³';
    }
    const choix = valeurs.map((v) => `${v} ${unite}`);
    const payload = makeQcm(choix, choix[0],
        `<p>${situation}</p>${RAPPEL_BROUILLON}<p><b>${question}</b></p>`, opts(L_FACT));
    payload._meta.params = {
        grandeur, support: support.nom, valeur: x, k: kStr(kk), ecriture, reponse: cle,
        distracteurs: distracteurs.map((d) => ({ code: d.code, valeur: d.valeur })),
    };
    return calculatrice(payload);
};

const genFacteur = () => {
    const grandeur = randomChoice(GRANDEURS);
    const k = randomChoice(K_QCM[grandeur]);
    const x = randInt(2, X_MAX[grandeur]);
    const support = grandeur === 'volume' ? randomChoice(SOLIDES) : randomChoice(FIGURES);
    return payloadFacteur(grandeur, x, k, support, tirerEcriture({ n: k, d: 1 }));
};

// ═════════ Carte 4 : « Le pavé réduit ou agrandi » (Saisie trous, l'exemple d'énoncé l. 347-350) ═════════

/** Les rapports du pavé : tiers (le plafond), moitié, quart, double, triple. */
export const RAPPORTS_PAVE = [{ n: 1, d: 3 }, { n: 1, d: 2 }, { n: 1, d: 4 }, { n: 2, d: 1 }, { n: 3, d: 1 }];
/** Le pavé du plafond, sacrifié dans tous les ordres. */
export const SACRIFIE_PAVE = [12, 6, 4];
const DIM_MAX = 12;
const VOLUME_PAVE_IMAGE_MAX = 9999;
const SOUS_TYPES = ['arete', 'face', 'volume'];

const estSacrifiePave = (L, l, h) => [L, l, h].slice().sort((a, b) => b - a).join(',') === SACRIFIE_PAVE.join(',');

/** Dimensions valides : L > l > h, toutes multiples de d, entre 2 et 12, jamais (12 ; 6 ; 4), volume image ≤ 9 999. */
const dimsValides = (L, l, h, k) => [L, l, h].every((v) => Number.isInteger(v) && v >= 2 && v <= DIM_MAX && v % k.d === 0)
    && L > l && l > h && !estSacrifiePave(L, l, h)
    && (L * l * h * k.n ** 3) / k.d ** 3 <= VOLUME_PAVE_IMAGE_MAX;

const REPLI_PAVE = { '1/3': [9, 6, 3], '1/2': [10, 8, 6], '1/4': [12, 8, 4], 2: [6, 5, 3], 3: [6, 5, 3] };

const tirerDimsPave = (k) => {
    const vals = [];
    for (let v = k.d; v <= DIM_MAX; v += k.d) if (v >= 2) vals.push(v);
    for (let essai = 0; essai < 200; essai++) {
        const [L, l, h] = shuffle(vals).slice(0, 3).sort((a, b) => b - a);
        if (dimsValides(L, l, h, k)) return { L, l, h };
    }
    const [L, l, h] = REPLI_PAVE[kStr(k)];
    return { L, l, h };
};

/** Une longueur, une aire ou un volume du pavé, puis la même grandeur du nouveau pavé (k, k², k³). */
const mulK = (v, k, e) => (v * k.n ** e) / k.d ** e;

export const tirerPave = () => {
    const k = randomChoice(RAPPORTS_PAVE);
    const { L, l, h } = tirerDimsPave(k);
    return { k, L, l, h, sousType: randomChoice(SOUS_TYPES), detail: randInt(0, 2), ecriture: tirerEcriture(k) };
};

const payloadPave = ({ k, L, l, h, sousType, detail, ecriture }) => {
    if (!dimsValides(L, l, h, k) || !SOUS_TYPES.includes(sousType)) throw new Error(`gen_180 pavé : tirage ${L} ${l} ${h} ${kStr(k)} ${sousType}`);
    const dims = { L, l, h };
    let phrase; let trous; let second; let quoi;
    if (sousType === 'arete') {
        const nom = ['L', 'l', 'h'][detail];
        const v = dims[nom];
        trous = [v, mulK(v, k, 1)];
        second = [v, (v / k.d) * k.n];                                  // réduire d'abord, multiplier ensuite
        quoi = `arête ${nom}`;
        phrase = `L'arête de longueur ${nom} mesure {{${trous[0]}}} cm ; ${apres(k)}, elle mesure {{${trous[1]}}} cm.`;
    } else if (sousType === 'face') {
        const faces = [
            { a: 'L', b: 'l', texte: 'La face de longueur L et de largeur l' },
            { a: 'L', b: 'h', texte: 'La face de dimensions L et h' },
            { a: 'l', b: 'h', texte: 'La face de dimensions l et h' },
        ];
        const f = faces[detail];
        const aire = dims[f.a] * dims[f.b];
        trous = [aire, mulK(aire, k, 2)];
        second = [dims[f.a] * dims[f.b], ((dims[f.a] / k.d) * k.n) * ((dims[f.b] / k.d) * k.n)];   // chaque dimension d'abord
        quoi = `face ${f.a} × ${f.b}`;
        phrase = `${f.texte} a pour aire {{${trous[0]}}} cm² ; ${apres(k)}, cette face a pour aire {{${trous[1]}}} cm².`;
    } else {
        const V = L * l * h;
        trous = [V, mulK(V, k, 3)];
        second = [L * l * h, ((L / k.d) * k.n) * ((l / k.d) * k.n) * ((h / k.d) * k.n)];
        quoi = 'volume';
        phrase = `Le volume du pavé est {{${trous[0]}}} cm³ ; ${apres(k)}, le volume du nouveau pavé est {{${trous[1]}}} cm³.`;
    }
    // Second chemin : les mêmes deux nombres par un autre calcul ; entiers ; les deux trous jamais égaux.
    if (trous.some((v, i) => !Number.isInteger(v) || v !== second[i]) || trous[0] === trous[1]) throw new Error(`gen_180 pavé : trous ${trous.join(' ')} ≠ ${second.join(' ')}`);
    if (phrase.includes('$')) throw new Error('gen_180 pavé : pas de KaTeX dans une phrase à trous');
    const texte = `<p>Un pavé droit a pour dimensions ${tex(`L = ${L}`)} cm, ${tex(`l = ${l}`)} cm et ${tex(`h = ${h}`)} cm. `
        + `${phraseRapport(k, ecriture, { pronom: true })}</p>${RAPPEL_BROUILLON}<p><b>Complète la phrase.</b></p>`;
    const payload = makeTexteTrou(phrase, texte, { ...opts(L_PAVE), mode: 'saisie' });
    payload.contenu.sousType = sousType;
    payload._meta.params = { L, l, h, k: kStr(k), ecriture, sousType, quoi, trous };
    return calculatrice(payload);
};

const genPave = () => payloadPave(tirerPave());

// ═════════ Registre ═════════

const GENERATORS = {
    aire_texte: genAire,
    volume_texte: genVolume,
    facteur_qcm_unique: genFacteur,
    pave_texte_trou_saisie: genPave,
};

const CAT = [
    { id: 'aire', label: L_AIRE, key: 'aire_texte', fmt: 'Saisie' },
    { id: 'volume', label: L_VOL, key: 'volume_texte', fmt: 'Saisie' },
    { id: 'facteur', label: L_FACT, key: 'facteur_qcm_unique', fmt: 'QCM' },
    { id: 'pave', label: L_PAVE, key: 'pave_texte_trou_saisie', fmt: 'Saisie trous' },
];

export const CATEGORIES = CAT.map((c) => ({ id: c.id, label: c.label, formatsList: [{ key: c.key, label: c.fmt }] }));

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const m = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = m.length ? randomChoice(m) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};

export const _internals = { GENERATORS, payloadAire, payloadVolume, payloadFacteur, payloadPave, dimsValides, MOTS };
